import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Clock, DollarSign, Loader2, ShoppingCart, XCircle } from 'lucide-react';
import { formatPrice } from '../../utils/helpers';
import {
  getOfferAmount,
  getOfferFromMessage,
  getOfferIdFromMessage,
  isAcceptedMessage,
  isCounterMessage,
  isProposalMessage,
  isRejectedMessage,
  isSellerCounterPending,
} from '../../utils/offerNormalize';

const statusClass = (status) => {
  const s = (status || 'PENDING').toLowerCase();
  return `mp-offer-card__status mp-offer-card__status--${s}`;
};

const OfferMessageCard = ({
  msg,
  isBuyer,
  actionOfferId,
  isAddingBidToCart,
  onAccept,
  onDecline,
  onAddToCart,
}) => {
  const { t } = useTranslation(['conversations', 'bids']);
  const offer = getOfferFromMessage(msg);
  const offerId = getOfferIdFromMessage(msg);
  const amount = getOfferAmount(offer);
  const status = offer?.status || 'PENDING';
  const isCounter = isCounterMessage(msg);
  const isProposal = isProposalMessage(msg);
  const isAccepted = isAcceptedMessage(msg);
  const isRejected = isRejectedMessage(msg);
  const showCounterActions = isSellerCounterPending(msg) && offerId;
  const showCart =
    isAccepted &&
    offerId &&
    status !== 'CONVERTED' &&
    !offer?.convertedToCart &&
    isBuyer;
  const showPending = isProposal && status === 'PENDING' && isBuyer;

  if (!isProposal && !isCounter && !isAccepted && !isRejected) return null;

  const label = isCounter
    ? t('counterOffer', { defaultValue: 'Counter offer' })
    : isAccepted
      ? t('offerAccepted', { defaultValue: 'Offer accepted', ns: 'bids' })
      : isRejected
        ? t('offerDeclined', { defaultValue: 'Offer declined', ns: 'bids' })
        : t('yourOffer', { defaultValue: 'Your offer' });

  return (
    <div className={`mp-offer-card ${isBuyer ? 'mp-offer-card--buyer' : 'mp-offer-card--seller'}`}>
      <div className="mp-offer-card__header">
        <DollarSign size={18} />
        <span className="mp-offer-card__label">{label}</span>
        <span className={statusClass(status)}>{status}</span>
      </div>
      {amount > 0 && <p className="mp-offer-card__amount">{formatPrice(amount)}</p>}
      {(msg.message || msg.text) && (
        <p className="mp-offer-card__note">{msg.message || msg.text}</p>
      )}

      {showCounterActions && (
        <div className="mp-offer-card__actions">
          <button
            type="button"
            className="mp-offer-card__btn mp-offer-card__btn--accept"
            disabled={actionOfferId === offerId}
            onClick={() => onAccept(offerId)}
          >
            {actionOfferId === offerId ? (
              <Loader2 size={16} className="spin" />
            ) : (
              <CheckCircle size={16} />
            )}
            {t('accept', { defaultValue: 'Accept', ns: 'bids' })}
          </button>
          <button
            type="button"
            className="mp-offer-card__btn mp-offer-card__btn--decline"
            disabled={actionOfferId === offerId}
            onClick={() => onDecline(offerId)}
          >
            <XCircle size={16} />
            {t('decline', { defaultValue: 'Decline', ns: 'bids' })}
          </button>
        </div>
      )}

      {showCart && (
        <button
          type="button"
          className="mp-offer-card__btn mp-offer-card__btn--cart"
          disabled={isAddingBidToCart}
          onClick={() => onAddToCart(offerId)}
        >
          <ShoppingCart size={16} />
          {t('addToCart', { ns: 'bids' })}
        </button>
      )}

      {showPending && (
        <p className="mp-offer-card__pending">
          <Clock size={14} />
          {t('offerPending', { defaultValue: 'Awaiting seller response', ns: 'bids' })}
        </p>
      )}
    </div>
  );
};

export default OfferMessageCard;
